import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronRight, CheckCircle2, Clock, MapPin, ChevronDown, PhoneCall } from "lucide-react";

import {
    COMPANY, TRUST_METRICS, PERFORMANCE_COUNTERS, SERVICES, WHY_VIBHUTI,
    INDUSTRIES, TECH_STACK, CLIENT_NAMES, CASE_STUDIES, HERO_IMAGE,
} from "@/lib/site-data";
import { Icon } from "@/lib/icon";
import Counter from "@/components/Counter";
import IndiaCorridor from "@/components/IndiaCorridor";
import InquiryForms from "@/components/InquiryForms";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0 },
};

/* --------------------------- HERO --------------------------- */
function Hero() {
    return (
        <section data-testid="home-hero" className="bg-[#0A1428] text-white overflow-hidden">
            {/* MOBILE / TABLET (< lg): stacked — image on top as hero, text + CTAs below */}
            <div className="lg:hidden">
                <div className="relative w-full h-[320px] sm:h-[440px] md:h-[560px]">
                    <img
                        src="/hero-banner.jpg"
                        alt="Vibhuti Logistics fleet"
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="eager"
                    />
                    {/* Subtle bottom fade so the seam into the dark text panel is smooth */}
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0A1428] to-transparent" />
                    {/* Scroll hint — a soft chevron that pulses down toward the text panel */}
                    <div
                        className="absolute inset-x-0 bottom-3 flex flex-col items-center pointer-events-none"
                        data-testid="hero-scroll-hint"
                        aria-hidden="true"
                    >
                        <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/85 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                            Scroll
                        </span>
                        <ChevronDown className="mt-1 h-5 w-5 text-white/90 animate-bounce drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]" strokeWidth={2} />
                    </div>
                </div>
                <div className="container-vl py-10 sm:py-14 md:py-16">
                    <motion.h1
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="font-display font-bold uppercase text-white leading-[0.95] tracking-tight"
                        style={{ fontSize: "clamp(2rem, 8.5vw, 3.75rem)" }}
                        data-testid="hero-headline"
                    >
                        Driving<br />
                        Supply Chains<br />
                        <span className="text-[#E63946]">Forward</span>
                    </motion.h1>
                    <div
                        className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm sm:text-base text-white"
                        data-testid="hero-services"
                    >
                        <span className="font-semibold">3PL</span>
                        <span className="text-[#E63946]">|</span>
                        <span>Warehousing</span>
                        <span className="text-[#E63946]">|</span>
                        <span>Transportation</span>
                        <span className="text-[#E63946]">|</span>
                        <span>4PL Solutions</span>
                    </div>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <Link
                            to="/contact"
                            data-testid="hero-cta-primary"
                            className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#E63946] text-white font-display font-semibold uppercase tracking-wider text-sm hover:bg-[#C1121F] transition-colors"
                        >
                            Get a Quote <ArrowRight className="h-4 w-4" />
                        </Link>
                        <Link
                            to="/services"
                            data-testid="hero-cta-secondary"
                            className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-transparent border-2 border-white text-white font-display font-semibold uppercase tracking-wider text-sm hover:bg-white hover:text-[#0A1428] transition-colors"
                        >
                            Explore Services
                        </Link>
                    </div>
                    <a
                        href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`}
                        data-testid="hero-call-pill"
                        className="mt-4 inline-flex items-center justify-center gap-2.5 w-full sm:w-auto px-6 py-3.5 bg-white/10 border border-white/25 backdrop-blur-sm text-white font-display uppercase tracking-wider text-sm hover:bg-white/20 transition-colors"
                    >
                        <PhoneCall className="h-4 w-4 text-[#F4A000]" strokeWidth={2} />
                        <span className="opacity-80">Call Us</span>
                        <span className="font-bold whitespace-nowrap">{COMPANY.phones[0]}</span>
                    </a>
                </div>
            </div>

            {/* DESKTOP (>= lg): overlay layout with image as background */}
            <div className="hidden lg:block relative min-h-[80vh]">
                <img
                    src="/hero-banner.jpg"
                    alt="Vibhuti Logistics fleet"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A1428] via-[#0A1428]/70 to-transparent" />
                <div className="relative container-vl min-h-[80vh] flex items-center py-24">
                    <div className="w-full max-w-2xl">
                        <motion.h1
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="font-display font-bold uppercase text-white leading-[0.95] tracking-tight"
                            style={{ fontSize: "clamp(2.75rem, 6vw, 5rem)" }}
                        >
                            Driving<br />
                            Supply Chains<br />
                            <span className="text-[#E63946]">Forward</span>
                        </motion.h1>
                        <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-1 text-base text-white">
                            <span className="font-semibold">3PL</span>
                            <span className="text-[#E63946]">|</span>
                            <span>Warehousing</span>
                            <span className="text-[#E63946]">|</span>
                            <span>Transportation</span>
                            <span className="text-[#E63946]">|</span>
                            <span>4PL Solutions</span>
                        </div>
                        <div className="mt-10 flex flex-row gap-4">
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#E63946] text-white font-display font-semibold uppercase tracking-wider text-sm hover:bg-[#C1121F] transition-colors"
                            >
                                Get a Quote <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/services"
                                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-transparent border-2 border-white text-white font-display font-semibold uppercase tracking-wider text-sm hover:bg-white hover:text-[#0A1428] transition-colors"
                            >
                                Explore Services
                            </Link>
                        </div>
                        <a
                            href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`}
                            className="mt-4 inline-flex items-center gap-2.5 px-6 py-3.5 bg-white/10 border border-white/25 backdrop-blur-sm text-white font-display uppercase tracking-wider text-sm hover:bg-white/20 transition-colors"
                        >
                            <PhoneCall className="h-4 w-4 text-[#F4A000]" strokeWidth={2} />
                            <span className="opacity-80">Call Us</span>
                            <span className="font-bold whitespace-nowrap">{COMPANY.phones[0]}</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Bottom edge accent */}
            <div className="relative h-1.5 bg-[#E63946]" />
        </section>
    );
}

/* --------------------------- TRUST BAR --------------------------- */
function TrustBar() {
    return (
        <section data-testid="home-trustbar" className="bg-[#101A2C] text-white border-y border-[#28406A]">
            <div className="container-vl grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-[#28406A]">
                {TRUST_METRICS.map((m) => (
                    <div key={m.label} className="px-6 py-7 text-center" data-testid={m.testId}>
                        <p className="font-display text-3xl md:text-4xl text-[#F4A000]">{m.value}</p>
                        <p className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#CBD5E1] mt-1">
                            {m.label}
                        </p>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* --------------------------- SERVICES --------------------------- */
function ServicesSnapshot() {
    return (
        <section data-testid="home-services" className="bg-[#F8FAFC] py-20 md:py-28">
            <div className="container-vl">
                <div className="grid grid-cols-12 gap-6 items-end mb-14">
                    <div className="col-span-12 md:col-span-7">
                        <p className="overline mb-4">Core Offerings</p>
                        <h2 className="heading-h2 text-[#101A2C]">
                            One partner.<br />Every node of your supply chain.
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5">
                        <p className="body-lg">
                            From dedicated transportation and managed warehousing to full 4PL orchestration — Vibhuti is the single point of accountability for your end-to-end logistics.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-5">
                    {SERVICES.map((s, i) => {
                        const span = s.featured ? "md:col-span-2" : "md:col-span-1";
                        const isHero = i === 0;
                        return (
                            <motion.div
                                key={s.slug}
                                variants={fadeUp}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.4, delay: i * 0.04 }}
                                className={`card-flat ${span} ${isHero ? "bg-[#28406A] text-white border-[#28406A] hover:shadow-[4px_4px_0_#F4A000]" : ""} p-7 flex flex-col gap-4`}
                                data-testid={`service-card-${s.slug}`}
                            >
                                <div className={`w-12 h-12 inline-flex items-center justify-center ${isHero ? "bg-[#F4A000] text-[#101A2C]" : "bg-[#E9EEF3] text-[#28406A]"}`}>
                                    <Icon name={s.icon} className="h-6 w-6" strokeWidth={1.5} />
                                </div>
                                <h3 className={`font-display uppercase text-xl ${isHero ? "text-white" : "text-[#101A2C]"}`}>
                                    {s.title}
                                </h3>
                                <p className={`text-sm leading-relaxed ${isHero ? "text-[#CBD5E1]" : "text-[#4B6390]"}`}>
                                    {s.teaser}
                                </p>
                                <Link
                                    to="/services"
                                    data-testid={`service-link-${s.slug}`}
                                    className={`mt-auto inline-flex items-center gap-1 text-[10px] sm:text-xs uppercase tracking-wider font-bold whitespace-nowrap ${isHero ? "text-[#F4A000]" : "text-[#28406A]"}`}
                                >
                                    Learn more <ChevronRight className="h-3 w-3" />
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- WHY VIBHUTI --------------------------- */
function WhyVibhuti() {
    return (
        <section data-testid="home-why" className="bg-white py-20 md:py-28">
            <div className="container-vl grid grid-cols-12 gap-10">
                <div className="col-span-12 lg:col-span-5">
                    <p className="overline mb-4">Why Vibhuti</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-6">
                        Built for the realities of Indian manufacturing.
                    </h2>
                    <p className="body-lg mb-8">
                        Two decades of automotive and engineering experience. Owned warehouses. Dedicated teams. Technology that gives you operational clarity — not buzzwords.
                    </p>
                    <Link to="/about" data-testid="why-cta" className="btn-navy">
                        Read Our Story
                    </Link>
                </div>
                <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {WHY_VIBHUTI.map((w, i) => (
                        <motion.div
                            key={w.title}
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.4, delay: i * 0.06 }}
                            className="bg-[#E9EEF3] p-6 border-l-4 border-[#F4A000]"
                            data-testid={`why-card-${i}`}
                        >
                            <Icon name={w.icon} className="h-7 w-7 text-[#28406A] mb-4" strokeWidth={1.5} />
                            <h3 className="font-display uppercase text-lg text-[#101A2C] mb-2">{w.title}</h3>
                            <p className="text-sm text-[#4B6390] leading-relaxed">{w.body}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- COUNTERS --------------------------- */
function PerformanceCounters() {
    return (
        <section data-testid="home-counters" className="bg-[#F4A000] py-20 md:py-24 relative overflow-hidden">
            <div className="absolute inset-0 grain-overlay opacity-30" />
            <div className="container-vl relative">
                <p className="overline mb-4 text-[#101A2C]">By the Numbers</p>
                <h2 className="heading-h2 text-[#101A2C] mb-12 max-w-3xl">
                    Two decades of operational excellence, measured.
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#101A2C]/15">
                    {PERFORMANCE_COUNTERS.map((c) => (
                        <div key={c.label} className="bg-[#F4A000] p-4 sm:p-7 md:p-9 min-w-0 overflow-hidden" data-testid={c.testId}>
                            <p className="font-display font-bold text-[#101A2C] whitespace-nowrap" style={{ fontSize: "clamp(1.5rem, 5vw, 4.2rem)", lineHeight: 1 }}>
                                <Counter to={c.value} suffix={c.suffix} />
                            </p>
                            <p className="mt-3 text-[10px] sm:text-xs md:text-sm uppercase tracking-[0.2em] text-[#101A2C]/80 font-semibold">
                                {c.label}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- NETWORK MAP --------------------------- */
function NetworkSection() {
    return (
        <section data-testid="home-network" className="bg-[#101A2C] text-white py-20 md:py-28 grain-overlay">
            <div className="container-vl relative grid grid-cols-12 gap-10 items-center">
                <div className="col-span-12 lg:col-span-5">
                    <p className="overline mb-4">Network</p>
                    <h2 className="heading-h2 text-white mb-6">
                        The Pune–Aurangabad<br />industrial backbone.
                    </h2>
                    <p className="text-[#CBD5E1] leading-relaxed mb-8">
                        Vibhuti's core corridor connects two of India's most active manufacturing clusters — with on-ground operations, dedicated fleets and warehousing at both anchors. Pan-India movements are executed through Vibhuti Transways Combines.
                    </p>
                    <ul className="space-y-3 mb-10">
                        {[
                            "Owned warehouses at Waluj (Aurangabad) & Chakan (Pune)",
                            "Dedicated fleet pool on the corridor",
                            "Pan-India network through combine partners",
                            "Real-time GPS visibility on every consignment",
                        ].map((p) => (
                            <li key={p} className="flex items-start gap-3 text-sm text-[#CBD5E1]">
                                <CheckCircle2 className="h-5 w-5 text-[#F4A000] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                                {p}
                            </li>
                        ))}
                    </ul>
                    <Link to="/network" data-testid="network-cta" className="btn-primary">
                        Explore Network <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="col-span-12 lg:col-span-7">
                    <div className="bg-white p-4 md:p-6 border border-[#28406A]">
                        <IndiaCorridor testId="home-india-map" />
                    </div>
                </div>
            </div>
        </section>
    );
}

/* --------------------------- INDUSTRIES --------------------------- */
function IndustriesGrid() {
    return (
        <section data-testid="home-industries" className="bg-white py-20 md:py-28">
            <div className="container-vl">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                    <div>
                        <p className="overline mb-4">Industries We Serve</p>
                        <h2 className="heading-h2 text-[#101A2C]">Embedded in industry. Trusted by manufacturers.</h2>
                    </div>
                    <Link to="/industries" data-testid="industries-link" className="text-sm font-bold uppercase tracking-widest text-[#28406A] border-b-2 border-[#F4A000] pb-1 w-fit">
                        All Industries →
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {INDUSTRIES.map((ind, i) => (
                        <motion.div
                            key={ind.slug}
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            className="group relative h-72 overflow-hidden border border-[#CBD5E1]"
                            data-testid={`industry-card-${ind.slug}`}
                        >
                            <img
                                src={ind.image}
                                alt={ind.title}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#101A2C] via-[#101A2C]/60 to-transparent transition-opacity group-hover:from-[#101A2C]/95" />
                            <div className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                                <Icon name={ind.icon} className="h-8 w-8 text-[#F4A000] mb-3" strokeWidth={1.5} />
                                <h3 className="font-display uppercase text-xl mb-2">{ind.title}</h3>
                                <p className="text-sm text-[#CBD5E1] max-h-0 opacity-0 group-hover:max-h-32 group-hover:opacity-100 transition-all duration-300">
                                    {ind.body}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- CLIENT WALL --------------------------- */
function ClientWall() {
    const items = [...CLIENT_NAMES, ...CLIENT_NAMES];
    return (
        <section data-testid="home-clients" className="bg-[#E9EEF3] py-16 md:py-20 overflow-hidden">
            <div className="container-vl mb-10 text-center">
                <p className="overline mb-3">Trusted By Industry Leaders</p>
                <h2 className="heading-h3 text-[#101A2C]">45+ industrial clients across automotive, manufacturing & engineering</h2>
            </div>
            <div className="relative">
                <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#E9EEF3] to-transparent z-10 pointer-events-none" />
                <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#E9EEF3] to-transparent z-10 pointer-events-none" />
                <div className="marquee-track" data-testid="client-marquee">
                    {items.map((name, i) => (
                        <div
                            key={`${name}-${i}`}
                            className="flex-shrink-0 px-7 py-4 mx-2 bg-white border border-[#CBD5E1] font-display uppercase tracking-wider text-[#28406A] text-sm md:text-base whitespace-nowrap hover:bg-[#28406A] hover:text-white transition-colors"
                            data-testid={`client-logo-${i}`}
                        >
                            {name}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- CASE STUDY --------------------------- */
function CaseStudyPreview() {
    const cs = CASE_STUDIES[0];
    return (
        <section data-testid="home-casestudy" className="bg-white py-20 md:py-28">
            <div className="container-vl grid grid-cols-12 gap-10 items-start">
                <div className="col-span-12 lg:col-span-5">
                    <p className="overline mb-4">Case Study</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-4">{cs.title}</h2>
                    <div className="flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-[#4B6390] mb-6">
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{cs.duration}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Aurangabad · Chakan</span>
                    </div>
                    <p className="body-lg mb-8">{cs.body}</p>
                    <Link to="/clients" data-testid="casestudy-cta" className="btn-navy">
                        See All Clients
                    </Link>
                </div>
                <div className="col-span-12 lg:col-span-7 bg-[#101A2C] text-white p-8 md:p-10 corner-bracket">
                    <p className="font-display uppercase text-[#F4A000] text-sm tracking-widest mb-6">Operational Highlights</p>
                    <ul className="space-y-4">
                        {cs.highlights.map((h) => (
                            <li key={h} className="flex items-start gap-3 text-sm md:text-base text-[#CBD5E1]">
                                <span className="font-mono text-[#F4A000] text-xs mt-1">[ ▪ ]</span>
                                {h}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

/* --------------------------- TECH STACK --------------------------- */
function TechStack() {
    return (
        <section data-testid="home-tech" className="bg-[#F8FAFC] py-20 md:py-24 border-y border-[#CBD5E1]">
            <div className="container-vl">
                <div className="grid grid-cols-12 gap-8 mb-12">
                    <div className="col-span-12 md:col-span-6">
                        <p className="overline mb-4">Technology Stack</p>
                        <h2 className="heading-h2 text-[#101A2C]">A control room for your shipments.</h2>
                    </div>
                    <div className="col-span-12 md:col-span-6">
                        <p className="body-lg">
                            Operational visibility from gate-in to ePOD. Every consignment is GPS-tracked, every dispatch is WMS-driven, every invoice is traceable.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
                    {TECH_STACK.map((t, i) => (
                        <div key={t.title} className="bg-white p-7 hover:bg-[#28406A] hover:text-white group transition-colors" data-testid={`tech-${i}`}>
                            <Icon name={t.icon} className="h-8 w-8 text-[#F4A000] mb-4" strokeWidth={1.5} />
                            <h3 className="font-display uppercase text-lg text-[#101A2C] group-hover:text-white mb-2">{t.title}</h3>
                            <p className="text-sm text-[#4B6390] group-hover:text-[#CBD5E1]">{t.body}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* --------------------------- LEAD CAPTURE --------------------------- */
function LeadCaptureSection() {
    return (
        <section data-testid="home-leadform" className="bg-[#E9EEF3] py-20 md:py-28">
            <div className="container-vl grid grid-cols-12 gap-10">
                <div className="col-span-12 lg:col-span-4">
                    <p className="overline mb-4">Get Started</p>
                    <h2 className="heading-h2 text-[#101A2C] mb-5">
                        Tell us what you move. We'll show you how.
                    </h2>
                    <p className="body-lg mb-8">
                        Whether you need a single dedicated truck or a fully orchestrated 4PL programme — choose your inquiry type and our supply-chain experts will respond within 24 hours.
                    </p>
                    <div className="bg-[#101A2C] text-white p-6 corner-bracket">
                        <p className="overline mb-2 text-[#F4A000]">Talk to us directly</p>
                        <a href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`} className="font-display text-2xl block">{COMPANY.phones[0]}</a>
                        <a href={`mailto:${COMPANY.email}`} className="text-sm text-[#CBD5E1] mt-1 inline-block">{COMPANY.email}</a>
                    </div>
                </div>
                <div className="col-span-12 lg:col-span-8">
                    <InquiryForms defaultTab="consultation" testId="home-inquiry-tabs" />
                </div>
            </div>
        </section>
    );
}

/* --------------------------- CTA --------------------------- */
function FinalCTA() {
    return (
        <section data-testid="home-finalcta" className="bg-[#28406A] text-white py-20 md:py-24 relative overflow-hidden grain-overlay">
            <div className="container-vl relative grid grid-cols-12 gap-8 items-center">
                <div className="col-span-12 md:col-span-8">
                    <p className="overline mb-3 text-[#F4A000]">Ready to optimize?</p>
                    <h2 className="heading-h2 text-white max-w-3xl">
                        Need a logistics partner that understands manufacturing?
                    </h2>
                </div>
                <div className="col-span-12 md:col-span-4 md:text-right">
                    <Link to="/contact" data-testid="final-cta" className="btn-primary">
                        Schedule a Consultation
                    </Link>
                </div>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <div data-testid="page-home">
            <Hero />
            <TrustBar />
            <ServicesSnapshot />
            <WhyVibhuti />
            <PerformanceCounters />
            <NetworkSection />
            <IndustriesGrid />
            <TechStack />
            <CaseStudyPreview />
            <ClientWall />
            <LeadCaptureSection />
            <FinalCTA />
        </div>
    );
}
